import React, { useState, useEffect } from 'react';
import { domId } from '../../utils/domIds';
import { User, Bell, Edit3, X, Save, Lock, ArrowLeft, Check, Trash2, BellOff, Plus, Phone, Send, Download, Smartphone } from 'lucide-react';
import { estaInstalada } from '../../utils/pwa';
import { APP_BUILD } from '../../appVersion';
import { AVAILABLE_COUNTRIES, COUNTRIES } from '../../utils/countryUtils';
import { useTracking } from '../../hooks/useTracking';
import { api } from '../../api/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import Bandera from '../ui/Bandera';

const API_BASE_URL          = process.env.REACT_APP_API_BASE_URL          || 'http://localhost:8000';
const TELEGRAM_BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'CholloMan_Bot';

// Las tiendas salían de esta lista fija, que además era española —Zara,
// PcComponentes, Mango_Oulet— mientras que la alerta se guardaba sin país y el
// backend la daba por argentina: se ofrecían tiendas de un catálogo y se
// buscaba en otro. Ahora salen de `/categories?pais=`, que devuelve las que
// existen de verdad en `productos` para ese país.

/**
 * La fecha de compilación en formato legible.
 *
 * Con hora y no sólo el día: en una jornada de despliegues seguidos, «2 de
 * septiembre» no distingue una versión de la siguiente, que es justo cuando
 * hace falta saber cuál tiene alguien cargada.
 */
const fechaDeVersion = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
const EMPTY_FORM = { product_name: '', target_price: '', stores: [] };

export const UserProfile = ({ user, onClose, onUpdateUser, initialSection = 'profile', onInstalarApp, country = 'AR' }) => {
  const isMobile = useIsMobile();
  const [activeSection, setActiveSection] = useState(initialSection);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    username:  user?.username  || '',
    full_name: user?.full_name || '',
    phone:     user?.phone     || '',
    pais_preferido: user?.pais_preferido || 'AR',
  });
  const [saveSuccess,      setSaveSuccess]      = useState(false);
  const [saveError,        setSaveError]        = useState('');
  const [savingProfile,    setSavingProfile]    = useState(false);
  const [linkingTelegram,  setLinkingTelegram]  = useState(false);
  const [verifyingLink,    setVerifyingLink]    = useState(false);
  const [verifyError,      setVerifyError]      = useState('');

  const {
    trackedProducts, loading: trackLoading, error: trackError,
    fetchTrackedProducts, addTrackedProduct, updateTrackedProduct,
    deleteTrackedProduct, toggleActive
  } = useTracking();

  const [trackForm, setTrackForm] = useState(EMPTY_FORM);
  const [trackFormError, setTrackFormError] = useState('');
  const [trackFormLoading, setTrackFormLoading] = useState(false);
  const [editingTrackId, setEditingTrackId] = useState(null);
  const [editTrackForm, setEditTrackForm] = useState(EMPTY_FORM);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Las tiendas del país que se está navegando, que es el que va a llevar la
  // alerta nueva. Si la petición falla se queda vacío y el formulario lo dice,
  // en vez de ofrecer una lista inventada.
  const [tiendas, setTiendas] = useState([]);

  useEffect(() => {
    let vigente = true;
    api.fetchCategories(country)
      .then(data => { if (vigente) setTiendas(data?.categories || []); })
      .catch(() => { if (vigente) setTiendas([]); });
    return () => { vigente = false; };
  }, [country]);

  useEffect(() => {
    if (user) setEditData({
      username:  user.username  || '',
      full_name: user.full_name || '',
      phone:     user.phone     || '',
      pais_preferido: user.pais_preferido || 'AR',
    });
  }, [user]);

  useEffect(() => {
    if (activeSection === 'tracking') fetchTrackedProducts();
  }, [activeSection, fetchTrackedProducts]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setSaveError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        username:  editData.username  || undefined,
        full_name: editData.full_name || undefined,
        phone:     editData.phone     || undefined,
        pais_preferido: editData.pais_preferido || undefined,
      };
      const resp = await fetch(`${API_BASE_URL}/users/profile`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body:    JSON.stringify(payload),
      });
      if (resp.ok) {
        const updated = await resp.json();
        if (onUpdateUser) onUpdateUser(updated);
        setIsEditing(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2500);
      } else {
        const err = await resp.json();
        setSaveError(err.detail || 'Error al guardar');
      }
    } catch {
      setSaveError('Error de conexión');
    } finally {
      setSavingProfile(false);
    }
  };

  const verifyTelegramLink = async () => {
    setVerifyingLink(true);
    setVerifyError('');
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const updated = await resp.json();
        if (updated.telegram_chat_id) {
          if (onUpdateUser) onUpdateUser(updated);
          setLinkingTelegram(false);
        } else {
          setVerifyError('Todavía no detectamos la vinculación. Asegurate de haber presionado "Iniciar" en el bot de Telegram.');
        }
      } else {
        setVerifyError('Error al verificar. Intentá de nuevo.');
      }
    } catch {
      setVerifyError('Error de conexión.');
    } finally {
      setVerifyingLink(false);
    }
  };

  const handleAddTrack = async (e) => {
    e.preventDefault();
    setTrackFormError('');
    if (!trackForm.product_name.trim()) { setTrackFormError('El nombre es obligatorio'); return; }
    setTrackFormLoading(true);
    try {
      await addTrackedProduct({
        product_name: trackForm.product_name.trim(),
        target_price: trackForm.target_price ? parseFloat(trackForm.target_price) : null,
        stores: trackForm.stores.length > 0 ? trackForm.stores : null,
        // Sin esto el backend la daba por argentina, aunque las tiendas que se
        // acababan de marcar fueran de otro catálogo.
        pais: country,
      });
      setTrackForm(EMPTY_FORM);
    } catch (err) { setTrackFormError(err.message); }
    finally { setTrackFormLoading(false); }
  };

  const handleEditTrackSave = async (id) => {
    await updateTrackedProduct(id, {
      product_name: editTrackForm.product_name.trim(),
      target_price: editTrackForm.target_price ? parseFloat(editTrackForm.target_price) : null,
      stores: editTrackForm.stores.length > 0 ? editTrackForm.stores : null
    });
    setEditingTrackId(null);
  };

  const storeToggle = (store, setter) =>
    setter(prev => ({
      ...prev,
      stores: prev.stores.includes(store)
        ? prev.stores.filter(s => s !== store)
        : [...prev.stores, store]
    }));

  const menuItems = [
    { id: 'profile', label: 'Mi Perfil', icon: User, description: 'Información personal' },
    /*{ id: 'tracking', label: 'Mi Seguimiento', icon: Bell, description: 'Alertas de productos', badge: trackedProducts.filter(p => p.active).length || null },*/
  ];

  /* ─── Profile section ─── */
  /**
   * Instalar o desinstalar la aplicación.
   *
   * Acá está siempre, esté o no instalada: el acceso flotante de la portada
   * desaparece una vez instalada, y entonces este es el único lugar donde
   * encontrar cómo sacarla.
   */
  const renderInstalacion = () => {
    const instalada = estaInstalada();
    return (
      <div
        id="user-profile-instalacion"
        className="perfil__tarjeta perfil__tarjeta--compacta"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: instalada ? '#f3f4f6' : '#fff1eb',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {instalada ? <Smartphone size={16} color="#6b7280" /> : <Download size={16} color="#FF4500" />}
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 className="perfil__titulo">
              {instalada ? 'Aplicación instalada' : 'Instalar la aplicación'}
            </h2>
            <p className="perfil__bajada">
              {instalada
                ? 'La estás usando instalada en este dispositivo'
                : 'Se abre como una app y funciona sin conexión'}
            </p>
          </div>
        </div>

        <button
          id="user-profile-instalar-boton"
          onClick={() => onInstalarApp?.()}
          className={`boton boton--ancho boton--alto ${
            instalada ? 'boton--fantasma' : 'boton--primario'}`}
        >
          {instalada ? <Trash2 size={14} /> : <Download size={14} />}
          {instalada ? 'Cómo desinstalarla' : 'Cómo instalarla'}
        </button>

        {/* Qué versión está corriendo AHORA en este dispositivo.
            Se lee del módulo compilado dentro del bundle, no de `version.json`:
            ese archivo dice qué versión hay en el servidor, que es justamente la
            que puede no coincidir. Para saber si a alguien le quedó una versión
            vieja, el dato útil es el de su copia. */}
        <div
          id="user-profile-version"
          style={{
            marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0',
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
            fontSize: '0.75rem', color: '#888',
          }}
        >
          <span>
            Versión <strong style={{ color: '#555' }}>v{APP_BUILD.version}</strong>
          </span>
          <span id="user-profile-version-fecha">{fechaDeVersion(APP_BUILD.builtAt)}</span>
        </div>
      </div>
    );
  };

  const renderProfile = () => (
    <div className="perfil__panel">
      <div className="perfil__encabezado">
        <h1 className="perfil__encabezado-titulo">Mi Perfil</h1>
        <p className="perfil__encabezado-bajada">Información y datos de tu cuenta</p>
      </div>

      <div className="perfil__tarjeta">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: '1.5rem' }}>
          <h2 className="perfil__titulo">Datos personales</h2>
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} className="boton boton--secundario">
              <Edit3 size={13} /> Editar
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setIsEditing(false)} className="boton boton--fantasma">
                <X size={13} /> Cancelar
              </button>
              <button onClick={handleSaveProfile} disabled={savingProfile} className="boton boton--primario">
                <Save size={13} /> {savingProfile ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          )}
        </div>

        {saveSuccess && (
          <div className="aviso aviso--exito">
            <Check size={15} /> Perfil actualizado correctamente
          </div>
        )}
        {saveError && (
          <div className="aviso aviso--error">
            {saveError}
          </div>
        )}

        {/* Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#FF4500', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
            {(user?.username || 'U').charAt(0).toUpperCase()}
          </div>
          <div>
            <p style={{ fontWeight: 700, color: '#111', margin: 0 }}>{user?.username}</p>
            <p style={{ color: '#888', fontSize: '0.8125rem', margin: '2px 0 0' }}>Miembro de CholloMan</p>
          </div>
        </div>

        <div className="perfil__grilla-campos">
          <FieldGroup label="Nombre completo">
            {isEditing
              ? <input value={editData.full_name} onChange={e => setEditData({ ...editData, full_name: e.target.value })} className="campo__control" placeholder="Tu nombre" />
              : <FieldValue>{user?.full_name || <span className="texto-vacio">Sin especificar</span>}</FieldValue>}
          </FieldGroup>

          <FieldGroup label="Nombre de usuario">
            {isEditing
              ? <input value={editData.username} onChange={e => setEditData({ ...editData, username: e.target.value })} className="campo__control" />
              : <FieldValue>{user?.username}</FieldValue>}
          </FieldGroup>

          <FieldGroup label="Correo electrónico" icon={<Lock size={12} className="icono-tenue" />} anchoTotal>
            <FieldValue muted>{user?.email}</FieldValue>
          </FieldGroup>

          <FieldGroup label="Teléfono" icon={<Phone size={12} className="icono-tenue" />} anchoTotal>
            {isEditing
              ? (
                <div>
                  <input
                    value={editData.phone}
                    onChange={e => setEditData({ ...editData, phone: e.target.value })}
                    className="campo__control"
                    placeholder="+54911XXXXXXXX"
                  />
                  <p className="perfil__nota">
                    Incluí el código de país (ej: +54911...)
                  </p>
                </div>
              )
              : <FieldValue muted={!user?.phone}>{user?.phone || 'Sin especificar'}</FieldValue>
            }
          </FieldGroup>

          {/* El país preferido no encierra al usuario en un catálogo: decide cuál
              ve al entrar y con cuál vienen marcadas sus alertas nuevas. Cada
              alerta puede apuntar al otro país, para quien vive en uno y sigue
              las ofertas del otro. */}
          {AVAILABLE_COUNTRIES.length > 1 && (
            <FieldGroup label="Ofertas de" anchoTotal>
              {isEditing
                ? (
                  <div>
                    <div className="chips">
                      {AVAILABLE_COUNTRIES.map(c => {
                        const activa = c.code === (editData.pais_preferido || 'AR');
                        return (
                          <button
                            key={c.code}
                            type="button"
                            id={`perfil-pais-${c.code}`}
                            onClick={() => setEditData({ ...editData, pais_preferido: c.code })}
                            aria-pressed={activa}
                            style={{
                              padding: '0 14px', minHeight: 40, borderRadius: 999,
                              border: `1px solid ${activa ? '#f97316' : '#e5e7eb'}`,
                              background: activa ? '#fff7ed' : '#fff',
                              color: activa ? '#c2410c' : '#6b7280',
                              fontSize: 13, fontWeight: activa ? 700 : 500, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                            }}
                          >
                            <Bandera code={c.code} size={16} />{c.name}
                          </button>
                        );
                      })}
                    </div>
                    <p className="perfil__nota">
                      El catálogo que ves al entrar. Cada alerta podés crearla para
                      el país que quieras.
                    </p>
                  </div>
                )
                : (
                  <FieldValue>
                    <Bandera code={user?.pais_preferido || 'AR'} size={16} />{' '}
                    {(COUNTRIES[user?.pais_preferido || 'AR'] || {}).name || 'Argentina'}
                  </FieldValue>
                )}
            </FieldGroup>
          )}
        </div>
      </div>

      {/* ── Sección Telegram ── */}
      <div className="perfil__tarjeta perfil__tarjeta--seguida">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#0088cc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Send size={16} color="#fff" />
          </div>
          <div>
            <h2 className="perfil__titulo">Notificaciones Telegram</h2>
            <p className="perfil__bajada">Recibí alertas de ofertas directamente en Telegram</p>
          </div>
        </div>

        {user?.telegram_chat_id ? (
          /* ── Ya vinculado ── */
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 14px', minWidth: 0 }}>
              <Check size={15} color="#16a34a" />
              <span style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: 600 }}>Telegram vinculado</span>
              <span style={{ fontSize: '0.75rem', color: '#888' }}>ID: {user.telegram_chat_id}</span>
            </div>
            <a
              href={`https://t.me/${TELEGRAM_BOT_USERNAME}?start=${user.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setLinkingTelegram(true)}
              className="boton boton--fantasma boton--enlace boton--chico"
            >
              Re-vincular
            </a>
          </div>
        ) : linkingTelegram ? (
          /* ── Esperando que el usuario presione Start en Telegram ── */
          <div>
            <div className="aviso aviso--atencion">
              <strong>Paso 1:</strong> Se abrió Telegram con el bot <strong>@{TELEGRAM_BOT_USERNAME}</strong>.<br />
              <strong>Paso 2:</strong> Presioná el botón <strong>"Iniciar" (Start)</strong> en Telegram.<br />
              <strong>Paso 3:</strong> Volvé aquí y hacé click en "Verificar vinculación".
            </div>
            {verifyError && (
              <div className="aviso aviso--error">
                {verifyError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={verifyTelegramLink}
                disabled={verifyingLink}
                className="boton boton--primario"
              >
                <Check size={14} />
                {verifyingLink ? 'Verificando...' : 'Verificar vinculación'}
              </button>
              <a
                href={`https://t.me/${TELEGRAM_BOT_USERNAME}?start=${user?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="boton boton--fantasma boton--enlace"
              >
                <Send size={14} /> Volver a abrir Telegram
              </a>
              <button onClick={() => { setLinkingTelegram(false); setVerifyError(''); }} className="boton boton--fantasma">
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          /* ── Sin vincular ── */
          <div>
            <p style={{ fontSize: '0.875rem', color: '#555', marginBottom: '1rem' }}>
              Vinculá tu cuenta con Telegram para recibir collages de ofertas cuando el bot encuentre productos o viajes que estás buscando.
            </p>
            <ol style={{ fontSize: '0.8125rem', color: '#666', paddingLeft: '1.25rem', marginBottom: '1rem', lineHeight: 1.8 }}>
              <li>Hacé click en <strong>"Vincular Telegram"</strong></li>
              <li>En Telegram, presioná el botón <strong>"Iniciar"</strong> (Start)</li>
              <li>Volvé aquí y hacé click en <strong>"Verificar vinculación"</strong></li>
            </ol>
            <a
              href={user?.id ? `https://t.me/${TELEGRAM_BOT_USERNAME}?start=${user.id}` : `https://t.me/${TELEGRAM_BOT_USERNAME}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setLinkingTelegram(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#0088cc', color: '#fff', border: 'none',
                borderRadius: 8, padding: '9px 18px', fontSize: '0.875rem',
                fontWeight: 600, cursor: 'pointer', textDecoration: 'none',
              }}
            >
              <Send size={14} />
              Vincular Telegram
            </a>
          </div>
        )}
      </div>
    </div>
  );

  /* ─── Tracking section ─── */
  const renderTracking = () => {
    const active = trackedProducts.filter(p => p.active).length;
    return (
      <div className="perfil__panel">
        <div className="perfil__encabezado perfil__encabezado--fijo">
          <h1 className="perfil__encabezado-titulo">Mi Seguimiento</h1>
          <p className="perfil__encabezado-bajada">
            {active > 0
              ? `${active} producto${active > 1 ? 's' : ''} activo${active > 1 ? 's' : ''} — recibís alertas por Telegram`
              : 'Agrega productos y te avisaremos cuando haya oferta'}
          </p>
        </div>

        {/* Add form */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ebebeb', padding: '1.25rem', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#111', margin: '0 0 1rem' }}>Agregar producto</h2>
          <form onSubmit={handleAddTrack} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <input
              value={trackForm.product_name}
              onChange={e => setTrackForm(p => ({ ...p, product_name: e.target.value }))}
              placeholder="Nombre del producto (ej: Nike Air Max)"
              className="campo__control"
            />
            <input
              type="number" min="0" step="0.01"
              value={trackForm.target_price}
              onChange={e => setTrackForm(p => ({ ...p, target_price: e.target.value }))}
              placeholder="Precio objetivo en € (opcional)"
              className="campo__control"
            />
            <div>
              <p className="perfil__bajada" style={{ marginBottom: 6 }}>
                Tiendas de {(COUNTRIES[country] || {}).name || country} (vacío = todas)
              </p>
              {tiendas.length === 0 && (
                <p className="campo__ayuda campo__ayuda--pegada">
                  Todavía no hay tiendas cargadas para este país. La alerta va a buscar en todas.
                </p>
              )}
              <div className="chips">
                {tiendas.map(s => (
                  <button key={s} type="button"
                    onClick={() => storeToggle(s, setTrackForm)}
                    style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                      border: '1px solid', transition: 'all 0.15s',
                      background: trackForm.stores.includes(s) ? '#0a0a0a' : '#fff',
                      borderColor: trackForm.stores.includes(s) ? '#0a0a0a' : '#ddd',
                      color: trackForm.stores.includes(s) ? '#fff' : '#666'
                    }}
                  >{s}</button>
                ))}
              </div>
            </div>
            {trackFormError && <p style={{ color: '#E5002B', fontSize: '0.8rem', margin: 0 }}>{trackFormError}</p>}
            <button type="submit" disabled={trackFormLoading} className="boton boton--primario boton--ancho">
              <Plus size={14} /> {trackFormLoading ? 'Agregando...' : 'Agregar a seguimiento'}
            </button>
          </form>
        </div>

        {/* List */}
        {trackError && (
          <div className="aviso aviso--error aviso--pegado">{trackError}</div>
        )}

        {trackLoading && trackedProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#ccc' }}>Cargando...</div>
        ) : trackedProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 0', color: '#ccc' }}>
            <Bell size={32} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
            <p style={{ fontSize: '0.875rem' }}>Aún no tienes productos en seguimiento</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {trackedProducts.map(p => (
              <div key={p.id} style={{
                background: '#fff',
                border: '1px solid #ebebeb',
                borderRadius: 10,
                opacity: p.active ? 1 : 0.55,
                overflow: 'hidden'
              }}>
                {editingTrackId === p.id ? (
                  <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input value={editTrackForm.product_name} onChange={e => setEditTrackForm(f => ({ ...f, product_name: e.target.value }))} className="campo__control campo__control--chico" />
                    <input type="number" min="0" step="0.01" value={editTrackForm.target_price} onChange={e => setEditTrackForm(f => ({ ...f, target_price: e.target.value }))} placeholder="Precio objetivo (€)" className="campo__control campo__control--chico" />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {tiendas.map(s => (
                        <button key={s} type="button" onClick={() => storeToggle(s, setEditTrackForm)}
                          style={{ padding: '3px 9px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', border: '1px solid', background: editTrackForm.stores.includes(s) ? '#0a0a0a' : '#fff', borderColor: editTrackForm.stores.includes(s) ? '#0a0a0a' : '#ddd', color: editTrackForm.stores.includes(s) ? '#fff' : '#666' }}
                        >{s}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleEditTrackSave(p.id)} className="boton boton--primario boton--compacto"><Check size={12} /> Guardar</button>
                      <button onClick={() => setEditingTrackId(null)} className="boton boton--fantasma boton--compacto"><X size={12} /> Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div className="crece">
                      <p style={{ fontWeight: 600, color: '#111', fontSize: '0.875rem', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.product_name}</p>
                      <div className="chips" style={{ marginTop: 5 }}>
                        {p.target_price && (
                          <span className="insignia insignia--verde insignia--contorno">
                            Hasta €{p.target_price}
                          </span>
                        )}
                        {p.stores?.length > 0
                          ? p.stores.map(s => <span key={s} className="insignia">{s}</span>)
                          : <span className="texto-vacio texto-vacio--chico">Todas las tiendas</span>
                        }
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <IconBtn
                        onClick={() => toggleActive(p.id, p.active)}
                        title={p.active ? 'Pausar' : 'Activar'}
                        active={p.active}
                        activeColor="#FF4500"
                      >
                        {p.active ? <Bell size={14} /> : <BellOff size={14} />}
                      </IconBtn>
                      <IconBtn onClick={() => { setEditingTrackId(p.id); setEditTrackForm({ product_name: p.product_name, target_price: p.target_price ?? '', stores: p.stores || [] }); }}>
                        <Edit3 size={14} />
                      </IconBtn>
                      {deleteConfirmId === p.id ? (
                        <>
                          <IconBtn danger onClick={() => { deleteTrackedProduct(p.id); setDeleteConfirmId(null); }}><Check size={14} /></IconBtn>
                          <IconBtn onClick={() => setDeleteConfirmId(null)}><X size={14} /></IconBtn>
                        </>
                      ) : (
                        <IconBtn onClick={() => setDeleteConfirmId(p.id)}><Trash2 size={14} /></IconBtn>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="user-profile" className="perfil-panel">
      <div className="perfil-panel__marco">

        {/* Columna de navegación: al costado en escritorio, en una barra
            superior en el teléfono. El cambio es una media query y no un
            `isMobile ?`, así lo resuelve el navegador al girar el aparato sin
            que React vuelva a pintar el árbol entero. */}
        <div className="perfil-panel__lateral">
          <div className="perfil-panel__cabecera">
            {isMobile ? (
              <button onClick={onClose} className="perfil-panel__volver">
                <ArrowLeft size={17} /> Volver
              </button>
            ) : (
              <span className="perfil-panel__marca">Mi Cuenta</span>
            )}
            <button onClick={onClose} aria-label="Cerrar" className="perfil-panel__cerrar">
              <X size={18} />
            </button>
          </div>

          {/* User badge */}
          {/* `--sin-menu` depende de los datos, no del ancho: con una sola
              sección el menú de abajo no se dibuja y este borde quedaría
              partiendo la nada. La media query decide dónde importa. */}
          <div className={`perfil-panel__usuario${menuItems.length <= 1 ? ' perfil-panel__usuario--sin-menu' : ''}`}>
            <div className="perfil-panel__identidad">
              <div className="perfil-panel__inicial">
                {(user?.username || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="perfil-panel__nombres">
                <p className="perfil-panel__usuario-nombre">{user?.username}</p>
                <p className="perfil-panel__usuario-correo">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className={`perfil-panel__nav${menuItems.length <= 1 ? ' perfil-panel__nav--unica' : ''}`}>
            {menuItems.map(item => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  id={domId('user-profile-nav', item.id)}
                  onClick={() => setActiveSection(item.id)}
                  className={`perfil-nav-item${isActive ? ' perfil-nav-item--activa' : ''}`}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <Icon size={16} style={{ color: isActive ? '#FF4500' : '#888', flexShrink: 0 }} />
                  <div className="crece">
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: isActive ? '#fff' : '#333', display: 'block' }}>{item.label}</span>
                    <span style={{ fontSize: '0.7rem', color: isActive ? '#aaa' : '#bbb' }}>{item.description}</span>
                  </div>
                  {item.badge > 0 && (
                    <span style={{ background: '#FF4500', color: '#fff', fontSize: '0.65rem', fontWeight: 700, borderRadius: 10, padding: '2px 6px', flexShrink: 0 }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="perfil-panel__pie">
            <button onClick={onClose} className="perfil-panel__volver perfil-panel__volver--ancho">
              <ArrowLeft size={14} /> Volver a la tienda
            </button>
          </div>
        </div>

        {/* Content */}
        <div id="user-profile-contenido" className="perfil-panel__contenido">
          {activeSection === 'profile' ? (
            <>
              {renderProfile()}
              {renderInstalacion()}
            </>
          ) : renderTracking()}
        </div>
      </div>
    </div>
  );
};

/* ─── Shared style helpers ─── */
// Campos y botones viven en `paginas.css` (sección 30), compartidos con la
// pantalla de alertas: son los mismos controles, y tenerlos declarados dos
// veces ya había hecho que el mismo campo se viera distinto en cada una.

const FieldGroup = ({ label, children, icon, anchoTotal }) => (
  <div className={anchoTotal ? 'campo--ancho-total' : undefined}>
    <label className="campo__etiqueta campo__etiqueta--rotulo">
      {label} {icon}
    </label>
    {children}
  </div>
);

const FieldValue = ({ children, muted }) => (
  <div className={`campo__valor${muted ? ' campo__valor--vacio' : ''}`}>
    {children}
  </div>
);

const IconBtn = ({ onClick, children, title, active, activeColor = '#FF4500', danger }) => (
  <button
    onClick={onClick}
    title={title}
    className={`boton-icono${danger ? ' boton-icono--peligro' : ''}${active ? ' boton-icono--activo' : ''}`}
    // El color del estado activo llega por parámetro, así que es el único dato
    // que la hoja de estilos no puede saber. El resto —incluido el realce al
    // pasar por encima, que antes se escribía a mano con onMouseEnter y por eso
    // no se deshacía nunca en una pantalla táctil— vive en `paginas.css`.
    style={active ? { background: `${activeColor}15`, color: activeColor } : undefined}
  >
    {children}
  </button>
);
